import { defineStore } from 'pinia'
import type { LoadingType } from '@/types/loading-type'

/**
 * Loading 狀態 Store
 *
 * 以計數器追蹤並發請求數量，避免多個 API 同時進行時提早關閉 Loading。
 */
export const useLoadingStore = defineStore('loading', () => {
  const spinner: LoadingType = 'spinner'

  const state = ref<Record<string, { isLoading: boolean; count: number }>>({
    [spinner]: {
      isLoading: false,
      count: 0
    }
  })

  /** 全域 Spinner 是否顯示中 */
  const isSpinnerLoading = computed(
    (): boolean => !!state.value[spinner]?.isLoading
  )

  /** 各區域 Loading 狀態（排除 spinner） */
  const isAreaLoading = computed(() => {
    return Object.entries(state.value).reduce(
      (prev, [key, value]) => {
        if (key !== spinner) {
          prev[key] = value.isLoading
        }

        return prev
      },
      {} as Record<string, boolean>
    )
  })

  /** 開啟指定類型的 Loading 並增加計數 */
  const addLoading = (type: string): void => {
    if (!state.value[type]) {
      state.value[type] = { isLoading: false, count: 0 }
    }

    const key = state.value[type]

    if (key) {
      key.isLoading = true
      key.count++
    }
  }

  /** 關閉指定類型的 Loading（延遲 500ms 避免閃爍） */
  const closeLoading = (type: string): void => {
    const key = state.value[type]

    if (key) {
      // 延遲 500ms 關閉，避免 API 回傳過快時 Loading 閃爍
      setTimeout(() => {
        if (key.count > 0) {
          key.count--
        }

        // 所有並發請求都完成（count 歸零）才關閉 Loading
        if (key.count === 0) {
          key.isLoading = false
        }
      }, 500)
    }
  }

  return {
    /** 全域 Spinner 是否顯示中 */
    isSpinnerLoading,
    /** 各區域 Loading 狀態（排除 spinner） */
    isAreaLoading,
    /** 開啟指定類型的 Loading 並增加計數 */
    addLoading,
    /** 關閉指定類型的 Loading（延遲 500ms 避免閃爍） */
    closeLoading
  }
})
